import { getCenterStyles, getFluidBoxStyles } from "vig-ui-library";
import { extensionTester } from "../Utilities/file";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { Axios } from "axios";
import { FaFile, FaFilePdf } from "react-icons/fa6";
import { useEffect, useRef, useState } from "react";
import { IoRefresh } from "react-icons/io5";
import { RequestApiRouteConfig, getRequestBasePath } from "../Api/routeConfig";

type ImageContainerProps = {
    isPreview: boolean;
    file: RequestFileDto;
    requestModuleUrl: string;
    axiosInstance: Axios;
    apiRouteConfig?: RequestApiRouteConfig;
};

const styles: React.CSSProperties = {
    ...getFluidBoxStyles(),
    objectPosition: "center",
};

const PLACEHOLDER_BG = "grey.50";
const PLACEHOLDER_ICON = "#9e9e9e";
const PDF_PREVIEW_ICON = "#000000";

function LoadFailurePlaceholder(props: { onRetry?: (e: React.MouseEvent) => void }) {
    const { onRetry } = props;
    return (
        <Box
            sx={{
                ...getFluidBoxStyles(),
                bgcolor: PLACEHOLDER_BG,
                ...getCenterStyles(),
                position: "relative",
            }}
        >
            <FaFile size={16} color={PLACEHOLDER_ICON} aria-hidden />
            {onRetry && (
                <IconButton
                    size="small"
                    sx={{ position: "absolute", bottom: 4, right: 4 }}
                    onClick={onRetry}
                    aria-label="Skúsiť znova načítať"
                >
                    <IoRefresh />
                </IconButton>
            )}
        </Box>
    );
}

function PreviewImageBody({ fileUrl }: { fileUrl: string }) {
    const [broken, setBroken] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    if (broken) {
        return (
            <LoadFailurePlaceholder
                onRetry={(e) => {
                    e.stopPropagation();
                    setBroken(false);
                    setReloadKey((k) => k + 1);
                }}
            />
        );
    }

    return (
        <img
            key={reloadKey}
            src={fileUrl}
            alt=""
            style={{ ...styles, objectFit: "cover" }}
            onError={() => setBroken(true)}
        />
    );
}

export const ImageContainer = (props: ImageContainerProps) => {

    console.log("ImageContainer updated");
    const { file, isPreview, requestModuleUrl, axiosInstance, apiRouteConfig } = props;

    const extension = file.extension || "";
    const isPdf = extensionTester.fileIsPdf(extension);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!file.uploadCompleted || !file.id) {
            setBlobUrl((prev) => {
                if (prev) window.URL.revokeObjectURL(prev);
                return null;
            });
            setLoading(false);
            setError(false);
            return;
        }

        let cancelled = false;

        const loadBlobUrl = async (attempt: number) => {
            try {
                if (cancelled) return;
                setLoading(true);
                setError(false);

                const requestBasePath = getRequestBasePath(apiRouteConfig);
                const url = `${requestModuleUrl}/${requestBasePath}/streamfile?id=${file.id}`;
                const response = await axiosInstance.get(url, { responseType: "blob" });
                const nextBlob = new Blob([response.data], { type: response.headers["content-type"] });
                const nextBlobUrl = window.URL.createObjectURL(nextBlob);

                if (cancelled) {
                    window.URL.revokeObjectURL(nextBlobUrl);
                    return;
                }

                setBlobUrl((prev) => {
                    if (prev) window.URL.revokeObjectURL(prev);
                    return nextBlobUrl;
                });
                setLoading(false);
            } catch (_err) {
                if (cancelled) return;
                if (attempt >= 5) {
                    setLoading(false);
                    setError(true);
                    return;
                }
                timeoutRef.current = window.setTimeout(() => {
                    void loadBlobUrl(attempt + 1);
                }, 1500);
            }
        };

        void loadBlobUrl(1);

        return () => {
            cancelled = true;
            if (timeoutRef.current !== undefined) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [apiRouteConfig, axiosInstance, file.id, file.uploadCompleted, requestModuleUrl, retryNonce]);

    useEffect(() => {
        return () => {
            if (blobUrl) {
                window.URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    if (!file.uploadCompleted || !file.id || loading) {
        return (
            <Box
                sx={{
                    ...getFluidBoxStyles(),
                    bgcolor: PLACEHOLDER_BG,
                    ...getCenterStyles(),
                }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error || !blobUrl) {
        return (
            <LoadFailurePlaceholder
                onRetry={(e) => {
                    e.stopPropagation();
                    setRetryNonce((prev) => prev + 1);
                }}
            />
        );
    }

    if (isPreview) return <ImagePreviewContainer extension={extension} fileUrl={blobUrl} />;

    if (isPdf) {
        return (
            <object data={blobUrl} type="application/pdf" style={styles}>
                <img src={blobUrl} style={styles} />
            </object>
        );
    }

    return <img src={blobUrl} style={{ ...styles, objectFit: "contain" }} />;
};

type ImagePreviewContainerProps = {
    extension: string;
    fileUrl: string;
};

const ImagePreviewContainer = (props: ImagePreviewContainerProps) => {
    const { extension, fileUrl } = props;
    const isImage = extensionTester.fileIsImage(extension);
    const isPdf = extensionTester.fileIsPdf(extension);

    if (isImage) {
        return <PreviewImageBody fileUrl={fileUrl} />;
    }

    return (
        <Box
            sx={{
                ...getFluidBoxStyles(),
                bgcolor: PLACEHOLDER_BG,
                ...getCenterStyles(),
            }}
        >
            {isPdf ? (
                <FaFilePdf size={16} color={PDF_PREVIEW_ICON} aria-hidden />
            ) : (
                <FaFile size={40} color={PLACEHOLDER_ICON} aria-hidden />
            )}
        </Box>
    );
};
